(() => {
  const normalize = (value) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');

  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`french_bites:${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `french_bites_${name}`, ...detail });
    }
  };

  const parseData = (root, selector, firstPosition) => {
    const node = root.querySelector(selector);
    if (!node) return [];
    try {
      const rows = JSON.parse(node.textContent || '[]');
      return rows.map((row, index) => ({
        position: firstPosition + index,
        french: row[0],
        english: row[1],
        cefr_level: row[2],
        category: row[3],
        audio_reference: row[4],
      }));
    } catch (error) {
      console.error('French Bites preposition data could not be loaded.', error);
      return [];
    }
  };

  const initialize = (root) => {
    const freeData = parseData(root, '[data-fpp-free-data]', 1);
    const memberData = parseData(root, '[data-fpp-member-data]', 101);
    const data = [...freeData, ...memberData];
    const isMember = root.dataset.member === 'true';
    const audioEnabled = root.dataset.audioEnabled === 'true';
    const audioPath = root.dataset.audioPath || '';
    const pageSize = Math.max(25, Number.parseInt(root.dataset.pageSize || '50', 10));
    const list = root.querySelector('[data-fpp-list]');
    const empty = root.querySelector('[data-fpp-empty]');
    const search = root.querySelector('[data-fpp-search]');
    const count = root.querySelector('[data-fpp-count]');
    const previous = root.querySelector('[data-fpp-previous]');
    const next = root.querySelector('[data-fpp-next]');
    const pageStatus = root.querySelector('[data-fpp-page-status]');
    const liveStatus = root.querySelector('[data-fpp-live-status]');
    let page = 1;
    let query = '';
    let currentAudio = null;
    let currentButton = null;
    let debounceTimer = null;

    if (!list || !search || !count || !previous || !next || !pageStatus) return;

    const stopAudio = () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      if (currentButton) {
        currentButton.setAttribute('aria-busy', 'false');
        currentButton.removeAttribute('aria-pressed');
      }
      currentAudio = null;
      currentButton = null;
    };

    const audioUrl = (reference) => {
      if (/^https:\/\//i.test(reference)) return reference;
      return `${audioPath}${encodeURIComponent(reference)}`;
    };

    const playAudio = async (item, button) => {
      stopAudio();
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-pressed', 'true');
      currentButton = button;
      currentAudio = new Audio(audioUrl(item.audio_reference));
      currentAudio.preload = 'none';
      currentAudio.addEventListener('playing', () => {
        button.setAttribute('aria-busy', 'false');
        if (liveStatus) liveStatus.textContent = `Playing ${item.french}`;
        track('preposition_pronunciation_played', { position: item.position, member: isMember });
      }, { once: true });
      currentAudio.addEventListener('ended', stopAudio, { once: true });
      currentAudio.addEventListener('error', () => {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${item.french} is temporarily unavailable.`;
      }, { once: true });
      try {
        await currentAudio.play();
      } catch (_error) {
        stopAudio();
        if (liveStatus) liveStatus.textContent = `Pronunciation for ${item.french} could not be played.`;
      }
    };

    const makeRow = (item) => {
      const row = document.createElement('li');
      row.className = 'fba__row';
      const position = document.createElement('span');
      position.className = 'fba__position';
      position.textContent = String(item.position).padStart(4, '0');
      const french = document.createElement('strong');
      french.className = 'fba__french';
      french.lang = 'fr';
      french.textContent = item.french;
      const translation = document.createElement('span');
      translation.className = 'fba__translation';
      translation.textContent = item.english;
      const metadata = [item.cefr_level, item.category].filter(Boolean).join(' · ');
      if (metadata) {
        const details = document.createElement('small');
        details.className = 'fba__level';
        details.textContent = metadata;
        translation.append(details);
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fba__audio button button--secondary';
      button.setAttribute('aria-label', `Listen to the pronunciation of ${item.french}`);
      button.setAttribute('aria-busy', 'false');
      button.disabled = !audioEnabled || (!audioPath && !/^https:\/\//i.test(item.audio_reference));
      const symbol = document.createElement('span');
      symbol.className = 'fba__audio-symbol';
      symbol.setAttribute('aria-hidden', 'true');
      symbol.textContent = '▶';
      button.append(symbol);
      if (!button.disabled) button.addEventListener('click', () => playAudio(item, button));
      row.append(position, french, translation, button);
      return row;
    };

    const render = () => {
      stopAudio();
      const filtered = query
        ? data.filter((item) => normalize(`${item.french} ${item.english} ${item.category || ''}`).includes(query))
        : data;
      const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
      page = Math.min(page, pageCount);
      const start = (page - 1) * pageSize;
      const visible = filtered.slice(start, start + pageSize);
      list.replaceChildren(...visible.map(makeRow));
      empty.hidden = filtered.length !== 0;
      count.textContent = filtered.length === data.length
        ? `${data.length} prepositions available`
        : `${filtered.length} matching prepositions`;
      pageStatus.textContent = filtered.length
        ? `Showing ${start + 1}–${start + visible.length} of ${filtered.length}`
        : 'No results';
      previous.disabled = page <= 1;
      next.disabled = page >= pageCount;
    };

    search.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        query = normalize(search.value.trim());
        page = 1;
        render();
        track('preposition_search', { has_query: Boolean(query), member: isMember });
      }, 150);
    });
    previous.addEventListener('click', () => {
      page -= 1;
      render();
      root.querySelector('[data-fpp-list-heading]')?.focus();
    });
    next.addEventListener('click', () => {
      page += 1;
      render();
      root.querySelector('[data-fpp-list-heading]')?.focus();
    });
    root.querySelector('[data-fpp-membership-cta]')?.addEventListener('click', () => track('membership_cta_clicked', { module: 'prepositions' }));
    root.querySelector('[data-fpp-sign-in]')?.addEventListener('click', () => track('sign_in_prompted', { module: 'prepositions' }));
    render();
    track('preposition_page_view', { member: isMember, available_words: data.length });
    if (!isMember) track('preposition_paywall_viewed', { locked_words: 100 });
  };

  document.querySelectorAll('[data-fpp-root]').forEach(initialize);
})();
